/* istanbul ignore file */
import { EventEmitter } from 'events';
import get from 'lodash/get';
import mapping from './mapping.db';

const language = 'und';

class CmsToCollegeCoreMapper extends EventEmitter {
  constructor(
    editorService,
    documents,
  ) {
    super();
    this.deferreds = {};
    this.documents = {};
    this.types = {};
    this.editorService = editorService;
    Object.keys(documents).forEach((d) => {
      this.documents[d] = {};
    });
  }

    init = (editorService, types) => {
      this.types = types;
      this.editorService = editorService;
    }

    addDocument = (name, document) => {
      this.documents[name] = { ...document };
      this.refresh(name);
    };

    mergeAll = (entities) => {
      const entitiesByType = {};
      Object.keys(entities).forEach((eid) => {
        const entity = entities[eid];
        if (!(entity._type in entitiesByType)) {
          entitiesByType[entity._type] = [];
        }
        entitiesByType[entity._type].push(entity);
      });

      mapping.forEach((rule) => {
        let typeNodes = entitiesByType[rule.entity] || [];
        if (rule.parentEntity) {
          const parentNid = get(entitiesByType, `${rule.parentEntity}[0].nid`, false);
          typeNodes = typeNodes.filter((e) => (e).host_id === parentNid);
        }
        if (rule.referredBy) {
          const entityIds = get(entitiesByType, rule.referredBy.entity, [])
            .map((e) => e[rule.referredBy.field])
            .map((f) => f && f[language])
            .filter((v) => v)
            .map((v) => v.map((i) => i.target_id))
            .reduce((acc, v) => acc.concat(v), [])
            .filter((nid) => nid)
            .map((nid) => `node${nid}`);
          typeNodes = typeNodes.filter((e) => entityIds.includes(e._eid));
        }

        let value = null;
        if (rule.field) {
          // Single value
          if (typeNodes.length === 1) {
            value = this.mapValue(
              get(typeNodes, `0.${rule.field}.${language}`),
              this.types[rule.entity].fields[rule.field],
              rule,
            );
          } else if (typeNodes.length > 1) {
            throw new Error(`Found too many nodes of the same type when applying rule: ${JSON.stringify(rule)}`);
          }
        } else if (rule.fields) {
          if (rule.type === 'list') {
            // list
            const fieldValues = [];
            rule.fields.forEach((fieldRule) => {
              let fieldValue;

              if (typeNodes.length > 0) {
                fieldValue = this.mapValue(
                  get(typeNodes[0], `${fieldRule.field}.${typeNodes[0].language}`),
                  this.types[rule.entity].fields[fieldRule.field],
                  fieldRule,
                );
              }
              if (fieldValue) {
                fieldValues.push(fieldValue);
              }
            });
            value = fieldValues;
            if (rule.transform) {
              value = rule.transform(value, null, this.types[rule.entity].fields[rule.field]);
            }
          } else if (rule.type === 'group') {
            // group
            value = typeNodes.reduce((rv, entity) => {
              const mappedVal = this.mapValue(
                entity[rule.groupby.field][language],
                this.types[rule.entity].fields[rule.groupby.field],
                rule.groupby,
              );
              rv[mappedVal] = (rv[mappedVal] || []);
              if (rule.fields.length === 1) {
                rv[mappedVal].push(
                  this.mapValue(
                    entity[rule.fields[0].field][language],
                    this.types[rule.entity].fields[rule.fields[0].field],
                    rule.fields[0],
                  ),
                );
              } else {
                rv[mappedVal].push(
                  rule.fields.reduce((current, entityField) => {
                    current[entityField.field] = this.mapValue(
                      entity[entityField.field][language],
                      this.types[rule.entity].fields[entityField.field],
                      entityField,
                    );
                    return current;
                  }, {}),
                );
              }
              return rv;
            }, {});
            if (rule.transform) {
              value = rule.transform(value, null, null);
            }
          } else {
            // Collection
            value = typeNodes.map((entity) => {
              const item = {};
              rule.fields.forEach((fieldRule) => {
                if (fieldRule.type === 'list') {
                  const fieldValues = [];
                  fieldRule.fields.forEach((listRule) => {
                    const fieldValue = this.mapValue(
                      entity[listRule.field][typeNodes[0].language],
                      this.types[fieldRule.entity].fields[listRule.field],
                      listRule,
                    );

                    if (fieldValue) {
                      fieldValues.push(fieldValue);
                    }
                  });
                  value = fieldValues;
                  if (fieldRule.transform) {
                    value = fieldRule.transform(value, null, this.types[fieldRule.entity].fields[fieldRule.field]);
                  }
                  this.putValue(item, fieldRule.documentPath, value);
                } else {
                  const fieldValue = this.mapValue(
                    entity[fieldRule.field][entity.language],
                    this.types[rule.entity].fields[fieldRule.field],
                    fieldRule,
                  );
                  this.putValue(item, fieldRule.documentPath, fieldValue);
                }
              });
              return item;
            });
          }
        }
        this.putValue(this.documents, rule.documentPath, value);
      });
      Object.keys(this.documents).forEach((name) => {
        this.refresh(name);
      });
    };

    mergeField = (type, eid, field, value, entity) => {
      window.parent.postMessage({
        event_id: 'change_registered',
        entity,
      }, '*');
      mapping.forEach((rule) => {
        if (rule.entity === type) {
          if (rule.parentEntity) {
            if (!entity.host_id) {
              return;
            }
            const parent = this.editorService.getEntityRepresentationByEid(
              `node${entity.host_id}`,
            );
            if (parent._type !== rule.parentEntity) {
              return;
            }
          }
          if (rule.referredBy) {
            const entityIds = this.editorService.getEntityRepresentationsByType(rule.referredBy.entity)
              .map((e) => e[rule.referredBy.field])
              .map((f) => f && f[language])
              .filter((v) => v)
              .map((v) => v.map((i) => i.target_id))
              .reduce((acc, v) => acc.concat(v), [])
              .filter((nid) => nid)
              .map((nid) => `node${nid}`);
            if (!entityIds.includes(eid)) {
              return;
            }
          }
          if (rule.field === field) {
            const documentValue = this.mapValue(
              value,
              this.types[type].fields[field],
              rule,
            );
            const documentName = this.putValue(this.documents, rule.documentPath, documentValue);
            this.refresh(documentName);
          } else if (rule.fields) {
            if (rule.type === 'list') {
              // list
              let fieldValues = [];
              rule.fields.forEach((fieldRule) => {
                let documentValue = null;
                if (fieldRule.field === field) {
                  documentValue = this.mapValue(
                    value,
                    this.types[rule.entity].fields[fieldRule.field],
                    fieldRule,
                  );
                } else {
                  documentValue = this.mapValue(
                    this.editorService.getValue(eid, fieldRule.field),
                    this.types[rule.entity].fields[fieldRule.field],
                    fieldRule,
                  );
                }

                if (documentValue) {
                  fieldValues.push(documentValue);
                }
              });

              if (rule.transform) {
                fieldValues = rule.transform(fieldValues, null, this.types[rule.entity].fields[rule.field]);
              }
              const documentName = this.putValue(this.documents, rule.documentPath, fieldValues);
              this.refresh(documentName);
            } else if (rule.type === 'group') {
              // group
              // TODO: use getEntityRepresentationsByType instead of getAllNodes
              const entities = this.editorService.getAllNodesByType(
                rule.entity, rule.parentEntity,
              );
              let groupValue = entities.reduce((rv, localEntity) => {
                if (rule.fields.length === 1) {
                  let val = this.editorService.getValue(localEntity.eid || localEntity._eid, rule.groupby.field);
                  const localEid = localEntity._eid || localEntity.eid;
                  if (entity._eid === localEid || entity.eid === localEid) {
                    val = value;
                  }
                  const mappedVal = this.mapValue(
                    val,
                    this.types[rule.entity].fields[rule.groupby.field],
                    rule.groupby,
                  );
                  rv[mappedVal] = (rv[mappedVal] || []);
                  rv[mappedVal].push(
                    this.mapValue(
                      this.editorService.getValue(localEntity.eid || localEntity._eid, rule.fields[0].field),
                      this.types[rule.entity].fields[rule.fields[0].field],
                      rule.fields[0],
                    ),
                  );
                } else {
                  const mappedVal = this.mapValue(
                    this.editorService.getValue(localEntity.eid || localEntity._eid, rule.groupby.field),
                    this.types[rule.entity].fields[rule.groupby.field],
                    rule.groupby,
                  );
                  rv[mappedVal] = (rv[mappedVal] || []);
                  rv[mappedVal].push(
                    rule.fields.reduce((current, entityField) => {
                      let currentVal = this.editorService.getValue(localEntity.eid || localEntity._eid, entityField.field);
                      const localEntityEid = localEntity._eid || localEntity.eid;
                      if (entity._eid === localEntityEid || entity.eid === localEntityEid) {
                        if (entityField.field === field || entityField.field === `field_${field}`) {
                          currentVal = value;
                        }
                      }
                      current[entityField.field] = this.mapValue(
                        currentVal,
                        this.types[rule.entity].fields[entityField.field],
                        entityField,
                      );
                      return current;
                    }, {}),
                  );
                }
                return rv;
              }, {});
              if (rule.transform) {
                groupValue = rule.transform(groupValue, null, this.types[rule.entity].fields[rule.field]);
              }
              const documentName = this.putValue(this.documents, rule.documentPath, groupValue);
              this.refresh(documentName);
            } else {
              // Collection
              let entities = this.editorService.getEntityRepresentationsByType(rule.entity);
              if (rule.parentEntity) {
                const parentEntities = this.editorService.getEntityRepresentationsByType(rule.parentEntity);
                const parentNid = ((parentEntities[0] || {})).nid || false;
                entities = entities.filter((e) => (e).host_id === parentNid);
              }
              const items = entities.map((localEntity) => {
                const localEid = localEntity._eid || localEntity.eid;
                if (entity._eid === localEid || entity.eid === localEid) {
                  if (localEntity[field]) {
                    localEntity[field][language] = value;
                  } else {
                    localEntity[`field_${field}`][language] = value;
                  }
                }

                const item = {};
                rule.fields.forEach((fieldRule) => {
                  if (fieldRule.type === 'list') {
                    const fieldValues = [];

                    fieldRule.fields.forEach((listRule) => {
                      const fieldValue = this.mapValue(
                        this.editorService.getValue(localEntity._eid || localEntity.eid, listRule.field),
                        this.types[fieldRule.entity].fields[listRule.field],
                        listRule,
                      );

                      if (fieldValue) {
                        fieldValues.push(fieldValue);
                      }
                    });
                    let val = fieldValues;
                    if (fieldRule.transform) {
                      val = fieldRule.transform(val, null, this.types[fieldRule.entity].fields[fieldRule.field]);
                    }
                    this.putValue(item, fieldRule.documentPath, val);
                  } else {
                    const fieldValue = this.mapValue(
                      this.editorService.getValue(localEntity._eid || localEntity.eid, fieldRule.field),
                      this.types[rule.entity].fields[fieldRule.field],
                      fieldRule,
                    );
                    this.putValue(item, fieldRule.documentPath, fieldValue);
                  }
                });
                return item;
              });
              const documentName = this.putValue(this.documents, rule.documentPath, items);
              this.refresh(documentName);
            }
          }
        }
      });
    };

    mapValue = (values, metadata, rule) => {
      if (!values) {
        return null;
      }
      let value;
      if (metadata.field_type === 'text' || metadata.field_type === 'text_long' || metadata.field_type === 'serial') {
        value = values.length === 1 ? values[0].value : null;
      } else if (metadata.field_type === 'number_integer') {
        value = values.length === 1 ? parseInt(values[0].value, 10) : null;
      } else if (metadata.field_type === 'number_float') {
        value = values.length === 1 ? values[0].value : null;
      } else if (metadata.field_type === 'list_boolean') {
        value = values.length === 1 ? (values[0].value === '1') : null;
      } else if (metadata.field_type === 'list_integer') {
        if (values.length === 1) {
          value = metadata.allowed_values[values[0].value];
          if (typeof value === 'string') {
            value = value.toUpperCase().trim().replace(/[/ -]/g, '_').replace(/[,]/g, '');
          }
        }
      } else if (metadata.field_type === 'datestamp') {
        value = values.length === 1 ? values[0].value : null;
      } else if (metadata.field_type === 'entityreference' && metadata.target_type === 'taxonomy_term') {
        value = values.map((item) => metadata.allowed_values[item.target_id]).filter((item) => item);
      } else if (metadata.field_type === 'taxonomy_term_reference') {
        value = values.length === 1 ? metadata.allowed_values[values[0].tid] : null;
      } else {
        throw new Error(`Unsupported value type for mapping: ${metadata.field_type}`);
      }
      if (rule.map && rule.map[value]) {
        value = rule.map[value];
      }
      if (rule.transform) {
        value = rule.transform(value, values, metadata);
      }
      return value;
    };

    putValue = (rootDocument, path, value) => {
      const keys = path.split('.');
      let subdocument = rootDocument;
      keys.forEach((key, i) => {
        if (subdocument !== null) {
          if (i === keys.length - 1) {
            subdocument[key] = value;
          } else if (!(key in subdocument)) {
            subdocument[key] = {};
          }
          subdocument = subdocument[key];
        }
      });
      return keys[0];
    };

    refresh = (name) => {
      this.emit('refresh');
    };

    getPromise = (name) => this.deferreds[name];
}

export default CmsToCollegeCoreMapper;
